import { Alert, Button, Empty, Space, Table } from "antd";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { getEnvironmentService } from "../../backend";
import { GetOutputsForResourceResponseOutputs } from "@microtica/ms-engine-sdk";
import MessageCard from "../../components/cards/MessageCard";
import Paragraph from "antd/es/typography/Paragraph";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { trackResourceDomainInit } from "../../backend/tracking/resource";
import AssignResourceDomainModal from "./AssignResourceDomainModal";
import { Component, IEnvDetails } from "../../types";


interface ComponentOutputsProps {
    env: IEnvDetails;
    component: Component;
}

const ComponentOutputs = ({ component, env }: ComponentOutputsProps) => {
    const currentProject = useRecoilValue(currentProjectState);
    const { projectId, envId, componentId } = useParams() as { projectId: string; envId: string; componentId: string; };
    const [loading, setLoading] = useState(true);
    const [outputs, setOutputs] = useState<GetOutputsForResourceResponseOutputs[] | undefined>([]);
    const [outputsErrorMessage, setOutputsErrorMessage] = useState<string>();
    const [domain, setDomain] = useState<{ accessUrl?: string; CNAME?: string; enableAssignDomain: boolean; }>();
    const [isModalVisible, setIsModalVisible] = useState(false);


    useEffect(() => {
        const loadData = async () => {
            const { data: { outputs } } = await getEnvironmentService().getOutputsForResource(envId, componentId, projectId)
                .catch(error => {
                    setOutputsErrorMessage(
                        error?.response?.data?.code === 409 ?
                            error.response.data.message :
                            "We are currently unable to get the outputs from this resource. Please try again later."
                    );
                    return { data: { outputs: undefined } };
                });

            if (outputs) {
                const accessUrl = outputs.find(o => o.key === "AccessUrl")?.value;
                const CNAME = outputs.find(o => o.key === "CNAME")?.value;
                setDomain({
                    accessUrl,
                    CNAME,
                    enableAssignDomain:
                        !!accessUrl &&
                        !!CNAME &&
                        new URL(accessUrl).hostname === CNAME.toLowerCase() &&
                        currentProject.paymentPlan?.id !== "FREE"
                });
            }

            setOutputs(outputs);
            setLoading(false);
        }

        loadData();
    }, []);

    const shouldShowInfoForApiResources = (key: string, value: string) => {
        // API gateway when deployed, auto-generates a domain name
        // When you assign a custom domain, API gateway generates a new auto-generated domain name which always starts with "d-<random string>"
        // We are using "d-" to check if the API domain is the one with assigned custom domain, or the default API gateway domain
        return key === "CNAME" && value.includes("execute-api") && !value.startsWith("d-");
    }

    return (
        <Space direction="vertical" size="large" className="flex-justify-space-between">
            <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                    Component Outputs
                </div>
                <div className="gray-text" style={{ fontWeight: 400, fontSize: 14 }}>
                    Dynamic outputs serve as a gateway for cross-component referencing, facilitating streamlined post-deployment connections and enabling optimal cloud orchestration.
                </div>
            </div>
            {
                !outputs ?
                    <MessageCard
                        type="error"
                        text={
                            <div>
                                {outputsErrorMessage}
                            </div>
                        }
                    />
                    :
                    <Table
                        size="small"
                        dataSource={outputs}
                        pagination={false}
                        loading={loading}
                        scroll={{ y: "calc(100vh - 445px)" }}
                        locale={{
                            emptyText:
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <>
                                            <div>
                                                Currently, there are no dynamic outputs available for this component.
                                            </div>
                                            <div>
                                                This may be due to the component not being deployed yet or not generating any output parameters upon deployment.
                                            </div>
                                        </>
                                    }
                                />
                        }}
                        columns={[
                            {
                                key: "key",
                                title: "Key",
                                dataIndex: "key",
                                render: key => key
                            },
                            {
                                title: "Value",
                                render: output => (
                                    <Space direction="horizontal" className='flex-justify-space-between'>
                                        <Paragraph style={{ margin: 0 }}
                                            copyable={
                                                output.value && !shouldShowInfoForApiResources(output.key, output.value) && {
                                                    text: output.value
                                                }}
                                        >
                                            {
                                                output.value.startsWith("http://") || output.value.startsWith("https://") ?
                                                    <a href={output.value} target="_blank" rel="noreferrer">
                                                        {output.value}
                                                    </a>
                                                    :
                                                    shouldShowInfoForApiResources(output.key, output.value) ?
                                                        <Alert
                                                            message="The value will become available once you assign a Custom Domain and initiate deployment of this resource. Please do NOT forget to update the DNS record once it becomes available."
                                                            type="info"
                                                            showIcon
                                                        /> :
                                                        output.value
                                            }
                                        </Paragraph>
                                        {
                                            output.key === "AccessUrl" && domain?.enableAssignDomain ?
                                                <Button
                                                    onClick={() => {
                                                        setIsModalVisible(true);
                                                        trackResourceDomainInit({
                                                            envId: envId!,
                                                            resourceName: component.name
                                                        });
                                                    }}
                                                    type="primary"
                                                >
                                                    Assign custom domain
                                                </Button> : null
                                        }
                                    </Space>
                                )
                            }
                        ]}
                    />
            }
            {
                isModalVisible && domain &&
                <AssignResourceDomainModal
                    visible={isModalVisible}
                    resourceName={component.name}
                    resourceVersion={component.version!}
                    domainCNAME={domain.CNAME!}
                    awsAccount={{
                        id: env.awsAccountId!,
                        // CloudFront only supports ACM certificates in the N. Virginia Region (us-east-1)
                        region: domain.CNAME!.endsWith("cloudfront.net") ?
                            "us-east-1" :
                            env.region!
                    }}
                    envId={envId!}
                    onCancel={() => setIsModalVisible(false)}
                />
            }
        </Space>
    )

}

export default ComponentOutputs;